import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Grid, Typography, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface ServiceItem {
  id: number;
  icon: string;
  heading: string;
  description: string;
  route?: string;
}

interface ServicesSectionProps {
  customServices?: ServiceItem[];
}

const DEFAULT_SERVICES: ServiceItem[] = [
  {
    id: 1,
    icon: "consultation",
    heading: "Consultation",
    description: "Strategy and discovery sprints, software consultancy, R&D, and digital transformation planning.",
  },
  {
    id: 2,
    icon: "ai",
    heading: "AI solutions",
    description: "NLP, computer vision, predictive analytics, and LLM-powered features built into your product.",
  },
  {
    id: 3,
    icon: "development",
    heading: "Software development",
    description: "Custom web, mobile, and backend systems engineered by senior developers with full DevOps support.",
    route: "/lms",
  },
  {
    id: 4,
    icon: "design",
    heading: "Design",
    description: "UI/UX, product design, interactive prototypes, and AR/VR experiences for web and mobile.",
    route: "/uxDesign",
  },
  {
    id: 5,
    icon: "emerging",
    heading: "Emerging tech",
    description: "Blockchain, machine learning, and data science applied to real product problems — not buzzwords.",
  },
  {
    id: 6,
    icon: "support",
    heading: "Support & maintenance",
    description: "Agile delivery, product management, QA, and staff augmentation for ongoing product partnerships.",
  },
  {
    id: 7,
    icon: "brainstorming",
    heading: "Brainstorming & analysis",
    description: "UX reviews, application re-engineering, validation sprints, and product design workshops.",
  },
  {
    id: 8,
    icon: "cloud",
    heading: "Cloud solutions",
    description: "Cloud migration, serverless architecture, cloud-native applications, and multi-cloud strategy.",
  },
  {
    id: 9,
    icon: "security",
    heading: "Security services",
    description: "Security assessment, penetration testing, compliance, and secure architecture design.",
  },
];

const ICONS: Record<string, React.ReactNode> = {
  consultation: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 9H17M7 13H17M12 17H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 19V5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19Z" stroke="currentColor" strokeWidth="1.5" />
      <path d="M15 3V7C15 7.55228 14.5523 8 14 8H10C9.44772 8 9 7.55228 9 7V3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  ai: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 8.5V4M12 8.5H8M12 8.5H16M7 21L4.5 17.5M4.5 17.5L2 14M4.5 17.5H8M7 11C7 9.34315 8.34315 8 10 8H14C15.6569 8 17 9.34315 17 11C17 12.6569 15.6569 14 14 14H10C8.34315 14 7 12.6569 7 11ZM16.5 21H7.5M17 21L19.5 17.5M19.5 17.5L22 14M19.5 17.5H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  development: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M17 7L21 12L17 17M7 7L3 12L7 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 3L10 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  design: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 16L8.58579 11.4142C9.36683 10.6332 10.6332 10.6332 11.4142 11.4142L16 16M14 14L15.5858 12.4142C16.3668 11.6332 17.6332 11.6332 18.4142 12.4142L20 14M14 8.5H14.01M3 19H21C21.5523 19 22 18.5523 22 18V6C22 5.44772 21.5523 5 21 5H3C2.44772 5 2 5.44772 2 6V18C2 18.5523 2.44772 19 3 19Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  emerging: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9.31 17.5L9.75 21M9.75 3L9.31 6.5M14.69 6.5L14.25 3M14.25 21L14.69 17.5M6.5 9.31L3 9.75M21 9.75L17.5 9.31M17.5 14.69L21 14.25M3 14.25L6.5 14.69M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  support: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M21 10H3M16 2V6M8 2V6M5.5 3.5H18.5C19.6046 3.5 20.5 4.39543 20.5 5.5V18.5C20.5 19.6046 19.6046 20.5 18.5 20.5H5.5C4.39543 20.5 3.5 19.6046 3.5 18.5V5.5C3.5 4.39543 4.39543 3.5 5.5 3.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  brainstorming: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 19.2857L3 21L4.71429 15M9 19.2857L16.2857 12L20.1429 15.8571L12.8571 23.1429M9 19.2857L12.8571 23.1429M4.71429 15L12 7.71429L15.8571 11.5714M4.71429 15L1 11.2857L8.28571 4L12.1429 7.85714" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  cloud: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 15C3 16.6569 4.34315 18 6 18H18C19.6569 18 21 16.6569 21 15C21 13.3431 19.6569 12 18 12H17.938C17.6591 9.15034 15.2027 7 12.25 7C9.29734 7 6.84094 9.15034 6.56262 12H6C4.34315 12 3 13.3431 3 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  security: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const Card = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  cursor: "pointer",
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
    "& .read-more": { gap: 10, color: "#1d648c" },
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { width: 22, height: 22 },
});

const CardHeading = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.62)",
  marginBottom: 18,
});

const ReadMore = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#2f81ad",
  transition: "gap 0.2s ease, color 0.2s ease",
  "& svg": { fontSize: 16 },
});

const ServicesSection: React.FC<ServicesSectionProps> = ({ customServices }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const services = customServices || DEFAULT_SERVICES;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      sx={{ pb: { xs: 8, md: 11 }, backgroundColor: "#fff" }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2.5}>
          {services.map((service, i) => (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <Box
                sx={{
                  height: "100%",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 0.45s ease ${
                    0.05 + i * 0.04
                  }s, transform 0.45s ease ${0.05 + i * 0.04}s`,
                }}
              >
                <Card onClick={() => service.route && navigate(service.route)}>
                  <IconBadge>{ICONS[service.icon]}</IconBadge>
                  <CardHeading variant="h3">{service.heading}</CardHeading>
                  <CardCopy>{service.description}</CardCopy>
                  <ReadMore className="read-more">
                    Read more <ArrowForwardIcon />
                  </ReadMore>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ServicesSection;
